import axios from 'utils/axios';
import { dispatch, store } from 'store';
import { createSlice } from '@reduxjs/toolkit';
import { LeaveApplication } from 'types/models/LeaveApplication';
import { addDays, differenceInCalendarDays, parseISO } from 'date-fns';
import { openSnackbar } from 'store/reducers/snackbar';

// ==============================|| LEAVE CALENDAR - COLOR MAPPING ||============================== //

// Auto-assign colors to leave types using a predefined palette
const LEAVE_TYPE_COLORS: Record<string, string> = {};
const COLOR_PALETTE = [
    '#1890ff', // blue
    '#52c41a', // green
    '#faad14', // gold
    '#722ed1', // purple
    '#eb2f96', // magenta
    '#13c2c2', // cyan
    '#fa541c', // volcano
    '#2f54eb', // geekblue
    '#a0d911', // lime
    '#fa8c16', // orange
];

function getColorForLeaveType(leaveType: string): string {
    if (!LEAVE_TYPE_COLORS[leaveType]) {
        const colorIndex = Object.keys(LEAVE_TYPE_COLORS).length % COLOR_PALETTE.length;
        LEAVE_TYPE_COLORS[leaveType] = COLOR_PALETTE[colorIndex];
    }
    return LEAVE_TYPE_COLORS[leaveType];
}

// ==============================|| LEAVE CALENDAR - RELIST TRANSFORM ||============================== //

function transformToCalendarEvents(list: any[]): any[] {
    return list.map((leave: any) => {
        const employeeName = leave.employee
            ? `${leave.employee.first_name ?? ''} ${leave.employee.last_name ?? ''}`.trim()
            : 'Unknown';

        // Calculate inclusive day count (both start and end dates count)
        const startDate = parseISO(leave.start_date);
        const parsedEnd = parseISO(leave.end_date);
        const inclusiveDays = differenceInCalendarDays(parsedEnd, startDate) + 1;
        const daysLabel = leave.duration === 'partial_day' ? 'Half Day' : `${inclusiveDays} day${inclusiveDays !== 1 ? 's' : ''}`;
        const title = `${employeeName} - ${leave.leave_type} (${daysLabel})`;

        // FullCalendar uses exclusive end dates, so add 1 day to end_date
        const endDate = addDays(parsedEnd, 1);

        return {
            id: leave.id,
            title,
            allDay: true,
            start: startDate,
            end: endDate,
            backgroundColor: getColorForLeaveType(leave.leave_type),
            textColor: '#fff',
            extendedProps: { leave },
        };
    });
}

// ==============================|| LEAVE CALENDAR - STATE ||============================== //

export interface LeaveCalendarProps {
    list: any[];
    relist: any[];
    selectedLeave: any | null;
    isOpen: boolean;
    isLoading: boolean;
    alertPopup: boolean;
    error: object | string | null;
}

const initialState: LeaveCalendarProps = {
    list: [],
    relist: [],
    selectedLeave: null,
    isOpen: false,
    isLoading: false,
    alertPopup: false,
    error: null,
};

// ==============================|| LEAVE CALENDAR - SLICE ||============================== //

const leaveCalendar = createSlice({
    name: 'leaveCalendar',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        hasError(state, action) {
            state.error = action.payload;
            state.isLoading = false;
        },
        setListSuccess(state, action) {
            state.list = action.payload.data ?? action.payload;
            state.relist = transformToCalendarEvents(state.list);
            state.isLoading = false;
        },
        addItemSuccess(state, action) {
            state.list = [...state.list, action.payload];
            state.relist = transformToCalendarEvents(state.list);
            state.isLoading = false;
        },
        editItemSuccess(state, action) {
            const foundIndex = state.list.findIndex((x: any) => x.id === action.payload.id);
            if (foundIndex !== -1) {
                state.list[foundIndex] = action.payload;
            }
            state.relist = transformToCalendarEvents(state.list);
            state.isLoading = false;
        },
        deleteItemSuccess(state, action) {
            state.list = state.list.filter((item: any) => item.id !== action.payload);
            state.relist = transformToCalendarEvents(state.list);
        },
        setSelectedLeave(state, action) {
            state.selectedLeave = action.payload;
        },
        openToggle(state, action) {
            state.isOpen = action.payload.isOpen;
        },
        alertPopupToggle(state, action) {
            state.alertPopup = action.payload.alertPopup;
        },
    },
});

export default leaveCalendar.reducer;

export const { openToggle, setSelectedLeave, alertPopupToggle } = leaveCalendar.actions;

// ==============================|| LEAVE CALENDAR - THUNKS ||============================== //

const API_ENDPOINT = 'hr/leave-application';

export function getApprovedLeaves(params: any = {}) {
    return async () => {
        dispatch(leaveCalendar.actions.setLoading(true));
        try {
            const response = await axios.get(API_ENDPOINT, {
                params: { search: 'approved', limit: 9999, ...params },
            });
            dispatch(leaveCalendar.actions.setListSuccess(response.data));
        } catch (error) {
            dispatch(leaveCalendar.actions.hasError(error));
        }
    };
}

export function addLeave(data: any) {
    return async () => {
        dispatch(leaveCalendar.actions.setLoading(true));
        try {
            const response = await axios.post(API_ENDPOINT, { ...data, status: 'approved' });
            dispatch(leaveCalendar.actions.addItemSuccess(response.data));
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Leave added successfully.',
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: false,
                })
            );
        } catch (error) {
            dispatch(leaveCalendar.actions.hasError(error));
        }
    };
}

export function editLeave(id: number, data: any) {
    return async () => {
        dispatch(leaveCalendar.actions.setLoading(true));
        try {
            const response = await axios.put(`${API_ENDPOINT}/${id}`, { ...data, status: 'approved' });
            dispatch(leaveCalendar.actions.editItemSuccess(response.data));
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Leave updated successfully.',
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: false,
                })
            );
        } catch (error) {
            dispatch(leaveCalendar.actions.hasError(error));
        }
    };
}

export function deleteLeave(id: number) {
    return async () => {
        try {
            await axios.delete(`${API_ENDPOINT}/${id}`);
            dispatch(leaveCalendar.actions.deleteItemSuccess(id));
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Leave deleted successfully.',
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: false,
                })
            );
        } catch (error) {
            dispatch(leaveCalendar.actions.hasError(error));
        }
    };
}
